/**
 * @description Merchant invoice list with URL-hydrated filters.
 * @dependencies invoiceService, customersService, react-router search params
 * @notes Reporting drill-down seeds customerId, dateFrom, dateTo, paymentStatus
 * from the query string. Paid + date range is interpreted as payment_date on the API.
 * StatusBadge prefers API isOverdue (company-local SQL) over laptop TZ.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { format } from 'date-fns';
import { useUser } from '../../contexts/AuthContext';
import invoiceService from '../../services/api/invoiceService';
import { customersService } from '../../services/api';
import {
  formatInvoiceDate
} from '../../utils/invoiceHelpers';
import { getCustomerDisplayName } from '../../utils/customerHelpers';
import StatusBadge from '../shared/StatusBadge';
import PermissionGate from '../shared/PermissionGate';
import { PERMISSIONS } from '../../constants/permissionMappings';

function InvoiceList() {
  const { user } = useUser();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [invoices, setInvoices] = useState([]);
  const [customers, setCustomers] = useState({});
  const [statistics, setStatistics] = useState({
    openInvoicesCount: 0,
    openInvoicesAmount: 0,
    overdueInvoicesCount: 0,
    overdueInvoicesAmount: 0,
    paidLast30DaysCount: 0,
    paidLast30DaysAmount: 0
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState(() => ({
    customerId: searchParams.get('customerId') || '',
    dateFrom: searchParams.get('dateFrom') || '',
    dateTo: searchParams.get('dateTo') || '',
    status: searchParams.get('status') || '',
    paymentStatus: searchParams.get('paymentStatus') || '',
    search: '',
    datePreset: searchParams.get('dateFrom') ? 'custom' : '',
    overdue: false
  }));
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 25,
    totalItems: 0,
    totalPages: 0
  });
  const [showCustomDatesModal, setShowCustomDatesModal] = useState(false);
  const [tempDateRange, setTempDateRange] = useState({ from: '', to: '' });
  const [isCustomerDropdownOpen, setIsCustomerDropdownOpen] = useState(false);
  const [isDatePresetDropdownOpen, setIsDatePresetDropdownOpen] = useState(false);
  const [openActionMenuId, setOpenActionMenuId] = useState(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const customerDropdownRef = useRef(null);
  const datePresetDropdownRef = useRef(null);
  const actionMenuRefs = useRef({});

  // Fetch customers for filter dropdown
  const fetchCustomers = useCallback(async () => {
    if (!user?.id) return;

    try {
      const customersData = await customersService.getMerchantCustomers();

      // Create customer lookup map
      const customerMap = {};
      if (customersData && Array.isArray(customersData)) {
        customersData.forEach(customer => {
          customerMap[customer.id] = customer;
        });
      }
      setCustomers(customerMap);
    } catch (err) {
      console.error('Error fetching customers:', err);
    }
  }, [user?.id]);

  // Fetch invoices with filters
  const fetchInvoices = useCallback(async () => {
    if (!user?.id) return;
    
    try {
      setLoading(true);
      setError(null);
      
      // Build query params
      const params = {
        page: pagination.page,
        limit: pagination.limit,
        ...filters
      };
      
      // Remove empty filters
      Object.keys(params).forEach(key => {
        if (params[key] === '') delete params[key];
      });
      
      const response = await invoiceService.getInvoices(params);
      
      // Phase 5.3: Handle consistent service response format
      const invoiceList = response.success ? response.invoices : response.invoices || [];
      const processedInvoices = invoiceList.map(inv => ({
        ...inv,
        // Use backend-provided values directly (backend already provides proper formatting)
        subtotal: parseFloat(inv.subtotal) || 0,
        totalAmount: parseFloat(inv.totalAmount) || 0,
        amountPaid: parseFloat(inv.amountPaid) || 0,
        // Calculate balance due from available data
        balanceDue: (parseFloat(inv.totalAmount) || 0) - (parseFloat(inv.amountPaid) || 0),
        balancePaid: parseFloat(inv.amountPaid) || 0,
        // Prioritize company name, fallback to personal name, then email
        customerDisplayName: inv.company || [inv.firstName, inv.lastName].filter(Boolean).join(' ') || inv.email || 'Unknown Customer'
      }));
      // Note: Backend now excludes deleted invoices, so no client-side filtering needed
      
      setInvoices(processedInvoices);
      setPagination(response.pagination || {
        page: 1,
        limit: 25,
        totalItems: 0,
        totalPages: 0
      });
    } catch (err) {
      console.error('Error fetching invoices:', err);
      setError('Failed to load invoices. Please try again.');
      setInvoices([]);
    } finally {
      setLoading(false);
    }
  }, [user?.id, filters, pagination.page, pagination.limit]);

  // Fetch statistics
  const fetchStatistics = useCallback(async () => {
    if (!user?.id) return;
    
    try {
      const stats = await invoiceService.getStatistics();
      
      // Map backend response to frontend expected format
      const counts = stats.counts || {};
      const amounts = stats.amounts || {};
      
      setStatistics({
        // Open invoices count (all non-void, non-deleted invoices with outstanding balance)
        openInvoicesCount: counts.open || 0,
        openInvoicesAmount: (amounts.totalUnpaidCents || 0) / 100, // Convert cents to dollars
        
        // Overdue invoices 
        overdueInvoicesCount: counts.overdue || 0,
        overdueInvoicesAmount: (amounts.totalOverdueCents || 0) / 100, // Convert cents to dollars
        
        // Recently paid (use paid count and amounts)
        paidLast30DaysCount: counts.paid || 0,
        paidLast30DaysAmount: (amounts.totalPaidCents || 0) / 100 // Convert cents to dollars
      });
    } catch (err) {
      console.error('Error fetching statistics:', err);
    }
  }, [user?.id]);

  // Initial data fetch
  useEffect(() => {
    const loadData = async () => {
      await Promise.all([
        fetchCustomers(),
        fetchStatistics(),
        fetchInvoices()
      ]);
    };
    loadData();
  }, [fetchCustomers, fetchStatistics, fetchInvoices]);

  // Add click-outside handlers for dropdowns
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (customerDropdownRef.current && !customerDropdownRef.current.contains(event.target)) {
        setIsCustomerDropdownOpen(false);
      }
      if (datePresetDropdownRef.current && !datePresetDropdownRef.current.contains(event.target)) {
        setIsDatePresetDropdownOpen(false);
      }
      // Handle action menu dropdowns
      if (openActionMenuId) {
        const menuRef = actionMenuRefs.current[openActionMenuId];
        if (menuRef && !menuRef.contains(event.target)) {
          setOpenActionMenuId(null);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [openActionMenuId]);

  // Clean industry-standard customer display - prioritize company name
  const getCustomerName = (invoice) => {
    // Use the preprocessed customerDisplayName that prioritizes company over personal name
    return invoice.customerDisplayName || 'Unknown Customer';
  };
  
  // Helper to get customer display for filter dropdown
  const getFilterCustomerDisplay = (customerId) => {
    if (!customerId) return 'All Customers';
    
    const customer = Object.values(customers).find(c => 
      c.id === customerId
    );
    
    if (!customer) return 'Unknown Customer';
    
    return getCustomerDisplayName(customer);
  };

  // Handle filter changes
  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPagination(prev => ({ ...prev, page: 1 })); // Reset to first page
  };

  // Handle date range presets
  const handleDatePreset = (preset) => {
    const today = new Date();
    const formatDate = (date) => format(date, 'yyyy-MM-dd');
    
    switch (preset) {
      case 'today':
        setFilters(prev => ({
          ...prev,
          dateFrom: formatDate(today),
          dateTo: formatDate(today)
        }));
        break;
      case 'week':
        const weekStart = new Date(today);
        weekStart.setDate(today.getDate() - today.getDay());
        setFilters(prev => ({
          ...prev,
          dateFrom: formatDate(weekStart),
          dateTo: formatDate(today)
        }));
        break;
      case 'month':
        const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);
        setFilters(prev => ({
          ...prev,
          dateFrom: formatDate(monthStart),
          dateTo: formatDate(today)
        }));
        break;
      case 'quarter':
        const quarter = Math.floor(today.getMonth() / 3);
        const quarterStart = new Date(today.getFullYear(), quarter * 3, 1);
        setFilters(prev => ({
          ...prev,
          dateFrom: formatDate(quarterStart),
          dateTo: formatDate(today)
        }));
        break;
      case 'year':
        const yearStart = new Date(today.getFullYear(), 0, 1);
        setFilters(prev => ({
          ...prev,
          dateFrom: formatDate(yearStart),
          dateTo: formatDate(today)
        }));
        break;
      default:
        // No action needed for default
        break;
    }
  };

  // Calculate invoice totals with tax and discount
  const calculateInvoiceTotal = (invoice) => {
    const subtotal = parseFloat(invoice.subtotal) || 0;
    const discountAmount = parseFloat(invoice.discountAmount) || 0;
    const taxAmount = parseFloat(invoice.taxAmount) || 0;
    const total = subtotal - discountAmount + taxAmount;
    return total;
  };

  // Calculate balance due
  const calculateBalanceDue = (invoice) => {
    const total = parseFloat(invoice.totalAmount) || calculateInvoiceTotal(invoice);
    const paidAmount = parseFloat(invoice.amountPaid) || 0;
    return total - paidAmount;
  };

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  // Handle void/delete actions
  const handleActionClick = (action, invoice) => {
    setSelectedInvoice(invoice);
    setConfirmAction(action);
    setShowConfirmModal(true);
    setOpenActionMenuId(null);
  };

  const handleDuplicateInvoice = async (invoice) => {
    try {
      const result = await invoiceService.duplicateInvoice(invoice.invoiceNumber);
      if (result?.invoice?.invoiceNumber) {
        navigate(`/merchant/invoices/${result.invoice.invoiceNumber}/edit`);
      }
    } catch (err) {
      console.error('Error duplicating invoice:', err);
      setError('Failed to duplicate invoice. Please try again.');
    }
  };

  const handleConfirmAction = async () => {
    if (!selectedInvoice || !confirmAction) return;

    try {
      if (confirmAction === 'void') {
        await invoiceService.voidInvoice(selectedInvoice.invoiceNumber);
      } else if (confirmAction === 'delete') {
        await invoiceService.deleteInvoice(selectedInvoice.invoiceNumber);
      }

      // Refresh the invoice list and statistics
      await Promise.all([
        fetchInvoices(),
        fetchStatistics()
      ]);

      // Close modal
      setShowConfirmModal(false);
      setSelectedInvoice(null);
      setConfirmAction(null);
    } catch (err) {
      console.error(`Error ${confirmAction}ing invoice:`, err);
      setError(`Failed to ${confirmAction} invoice. Please try again.`);
    }
  };

  // Render status badge
  const renderStatusBadge = (invoice) => {
    const status = invoice.status;
    const paymentStatus = invoice.paymentStatus;
    const dueDate = invoice.dueDate;

    return (
      <div className="flex flex-wrap items-center gap-2">
        <StatusBadge 
          type="invoice_status" 
          value={{
            paymentStatus,
            status,
            dueDate,
            isOverdue: invoice.isOverdue
          }}
          size="xs"
        />
      </div>
    );
  };

  // Render mobile invoice card
  const renderMobileInvoiceCard = (invoice) => {
    const isPaid = invoice.paymentStatus === 'paid';
    const isVoided = invoice.paymentStatus === 'voided';

    return (
      <div key={invoice.id} className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-4">
        <div className="flex justify-between items-start mb-3">
          <div className="flex flex-wrap gap-4 flex-1">
            {/* First Row - Invoice Number and Customer Name */}
            <div className="flex-1 min-w-[45%] sm:min-w-[120px] text-center flex flex-col justify-center">
              <h3 className="text-lg font-semibold text-gray-900">{invoice.invoiceNumber}</h3>
              <div className="mt-2 sm:hidden">
                <p className="text-sm text-gray-500">Due Date</p>
                <p className="text-base font-medium text-gray-900">{formatInvoiceDate(invoice.dueDate)}</p>
              </div>
            </div>
            
            <div className="flex-1 min-w-[45%] sm:min-w-[140px] text-center flex flex-col justify-center">
              <p className="text-base text-gray-600">{getCustomerName(invoice)}</p>
              <div className="mt-2 sm:hidden">
                <p className="text-sm text-gray-500">Balance Due</p>
                <p className="text-base font-medium text-gray-900">{formatCurrency(calculateBalanceDue(invoice))}</p>
              </div>
            </div>
            
            {/* Second Row on sm screens - Due Date and Balance Due */}
            <div className="hidden sm:flex sm:w-full sm:gap-4 md:w-auto md:contents">
              <div className="flex-1 min-w-[45%] md:min-w-[120px] flex flex-col items-center justify-center">
                <p className="text-sm text-gray-500">Due Date</p>
                <p className="text-base font-medium text-gray-900">{formatInvoiceDate(invoice.dueDate)}</p>
              </div>
              
              <div className="flex-1 min-w-[45%] md:min-w-[120px] flex flex-col items-center justify-center">
                <p className="text-sm text-gray-500">Balance Due</p>
                <p className="text-base font-medium text-gray-900">{formatCurrency(calculateBalanceDue(invoice))}</p>
              </div>
            </div>
          </div>
          <div className="flex items-center ml-4">
            <div className="scale-110">
              {renderStatusBadge(invoice)}
            </div>
          </div>
        </div>
        
        <div className="flex items-center gap-3">
          <button
            onClick={() => invoice.invoiceNumber && navigate(`/merchant/invoices/${invoice.invoiceNumber}`)}
            disabled={!invoice.invoiceNumber}
            className="flex-1 px-4 py-2 bg-brand text-white rounded-lg font-semibold hover:bg-brand-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            View
          </button>
          {!isPaid && !isVoided && invoice.invoiceNumber && (
            <button
              onClick={() => navigate(`/merchant/invoices/${invoice.invoiceNumber}/payment`)}
              className="flex-1 px-4 py-2 border border-brand text-gray-800 rounded-lg font-semibold hover:bg-brand-light transition-colors"
            >
              Receive Payment
            </button>
          )}
          <div className="relative" ref={el => actionMenuRefs.current[invoice.id] = el}>
            <button
              onClick={() => setOpenActionMenuId(openActionMenuId === invoice.id ? null : invoice.id)}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z" />
              </svg>
            </button>
            {openActionMenuId === invoice.id && (
              <div className="absolute right-0 bottom-full mb-2 w-48 bg-white rounded-md shadow-lg z-50 border border-gray-200">
                <div className="py-1">
                  <PermissionGate permission={PERMISSIONS.invoices.create}>
                    <button
                      onClick={() => { setOpenActionMenuId(null); handleDuplicateInvoice(invoice); }}
                      className="block w-full text-left px-4 py-2 text-sm text-gray-900 hover:bg-gray-50"
                    >
                      Duplicate Invoice
                    </button>
                  </PermissionGate>
                  <PermissionGate permission={PERMISSIONS.payments.void}>
                    <button
                      onClick={() => handleActionClick('void', invoice)}
                      className="block w-full text-left px-4 py-2 text-sm text-orange-600 hover:bg-orange-50"
                      disabled={isVoided}
                    >
                      {isVoided ? 'Already Voided' : 'Void Invoice'}
                    </button>
                  </PermissionGate>
                  <PermissionGate permission={PERMISSIONS.invoices.delete}>
                    <button
                      onClick={() => handleActionClick('delete', invoice)}
                      className="block w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                    >
                      Delete Invoice
                    </button>
                  </PermissionGate>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  // Render desktop invoice card
  const renderDesktopInvoiceCard = (invoice) => {
    const isPaid = invoice.paymentStatus === 'paid';
    const isVoided = invoice.paymentStatus === 'voided';
    const balanceDue = calculateBalanceDue(invoice);

    return (
      <div key={invoice.id} className="bg-white rounded-lg shadow-sm border border-gray-200 py-3 px-6 hover:shadow-md transition-shadow">
        <div className="flex items-center">
          {/* Created date and Invoice number - 15% */}
          <div className="w-[15%] text-center px-2">
            <p className="text-xs text-gray-500 mb-1">Created: {formatInvoiceDate(invoice.invoiceDate)}</p>
            <p className="text-sm font-semibold text-gray-900 truncate">{invoice.invoiceNumber}</p>
          </div>
          
          {/* Customer name - 20% with truncate for long names */}
          <div className="w-[20%] text-center px-2">
            <p className="text-sm font-medium text-gray-900 truncate" title={getCustomerName(invoice)}>
              {getCustomerName(invoice)}
            </p>
          </div>
          
          {/* Due date - 12% */}
          <div className="w-[12%] text-center px-2">
            <p className="text-xs text-gray-500">Due Date</p>
            <p className="text-sm font-medium text-gray-900">{formatInvoiceDate(invoice.dueDate)}</p>
          </div>
          
          {/* Balance due - 13% */}
          <div className="w-[13%] text-center px-2">
            <p className="text-xs text-gray-500">Balance Due</p>
            <p className="text-sm font-medium text-gray-900">{formatCurrency(balanceDue)}</p>
          </div>
          
          {/* Balance paid - 13% */}
          <div className="w-[13%] text-center px-2">
            <p className="text-xs text-gray-500">Balance Paid</p>
            <p className="text-sm font-medium text-gray-900">{formatCurrency(invoice.amountPaid || 0)}</p>
          </div>
          
          {/* Status - 10% */}
          <div className="w-[10%] flex justify-center px-2">
            {renderStatusBadge(invoice)}
          </div>
          
          {/* Actions - 17% with consistent layout */}
          <div className="w-[17%] flex items-center justify-end gap-3 px-2">
            {!isPaid && !isVoided && invoice.invoiceNumber && (
              <button
                onClick={() => navigate(`/merchant/invoices/${invoice.invoiceNumber}/payment`)}
                className="md:hidden text-sm text-brand hover:text-brand-hover font-medium whitespace-nowrap"
              >
                Receive payment
              </button>
            )}
            <button
              onClick={() => invoice.invoiceNumber && navigate(`/merchant/invoices/${invoice.invoiceNumber}`)}
              disabled={!invoice.invoiceNumber}
              className="text-sm text-gray-700 hover:text-gray-900 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            >
              View
            </button>
            <div className="relative" ref={el => actionMenuRefs.current[invoice.id] = el}>
              <button
                onClick={() => setOpenActionMenuId(openActionMenuId === invoice.id ? null : invoice.id)}
                className="p-1 hover:bg-gray-100 rounded"
              >
                <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z" />
                </svg>
              </button>
              {openActionMenuId === invoice.id && (
                <div className="absolute right-0 top-full mt-2 w-48 bg-white rounded-md shadow-lg z-50 border border-gray-200">
                  <div className="py-1">
                    <PermissionGate permission={PERMISSIONS.invoices.create}>
                      <button
                        onClick={() => { setOpenActionMenuId(null); handleDuplicateInvoice(invoice); }}
                        className="block w-full text-left px-4 py-2 text-sm text-gray-900 hover:bg-gray-50"
                      >
                        Duplicate Invoice
                      </button>
                    </PermissionGate>
                    <PermissionGate permission={PERMISSIONS.payments.void}>
                      <button
                        onClick={() => handleActionClick('void', invoice)}
                        className="block w-full text-left px-4 py-2 text-sm text-orange-600 hover:bg-orange-50"
                        disabled={isVoided}
                      >
                        {isVoided ? 'Already Voided' : 'Void Invoice'}
                      </button>
                    </PermissionGate>
                    <PermissionGate permission={PERMISSIONS.invoices.delete}>
                      <button
                        onClick={() => handleActionClick('delete', invoice)}
                        className="block w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                      >
                        Delete Invoice
                      </button>
                    </PermissionGate>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  if (loading && invoices.length === 0) {
    return (
      <div className="flex justify-center items-center py-10">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand"></div>
      </div>
    );
  }

  return (
    <div>
      {/* Metrics Cards - Responsive Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
        <div className="bg-gray-100 rounded-lg p-4 flex justify-between items-center">
          <div>
            <p className="text-sm text-gray-600 font-medium">{statistics.openInvoicesCount} Invoices Open</p>
            <p className="text-2xl font-bold mt-1">{formatCurrency(statistics.openInvoicesAmount)}</p>
          </div>
          <button 
            onClick={() => {
              setFilters({
                customerId: '',
                dateFrom: '',
                dateTo: '',
                status: '',
                paymentStatus: 'unpaid,partially_paid',
                search: '',
                datePreset: '',
                overdue: false
              });
              setPagination(prev => ({ ...prev, page: 1 }));
            }}
            className="text-sm font-medium text-gray-700 hover:text-gray-900">
            View
          </button>
        </div>

        <div className="bg-orange-100 rounded-lg p-4 flex justify-between items-center">
          <div>
            <p className="text-sm text-gray-600 font-medium">{statistics.overdueInvoicesCount} Invoices Overdue</p>
            <p className="text-2xl font-bold mt-1">{formatCurrency(statistics.overdueInvoicesAmount)}</p>
          </div>
          <button 
            onClick={() => {
              setFilters({
                customerId: '',
                dateFrom: '',
                dateTo: '',
                status: '',
                paymentStatus: 'unpaid,partially_paid',
                search: '',
                datePreset: '',
                overdue: true
              });
              setPagination(prev => ({ ...prev, page: 1 }));
            }}
            className="text-sm font-medium text-gray-700 hover:text-gray-900">
            View
          </button>
        </div>

        <div className="bg-green-100 rounded-lg p-4 flex justify-between items-center">
          <div>
            <p className="text-sm text-gray-600 font-medium">{statistics.paidLast30DaysCount} Paid (Last 30 Days)</p>
            <p className="text-2xl font-bold mt-1">{formatCurrency(statistics.paidLast30DaysAmount)}</p>
          </div>
          <button 
            onClick={() => {
              setFilters({
                customerId: '',
                dateFrom: '',
                dateTo: '',
                status: '',
                paymentStatus: 'paid',
                search: '',
                datePreset: '',
                overdue: false
              });
              setPagination(prev => ({ ...prev, page: 1 }));
            }}
            className="text-sm font-medium text-gray-700 hover:text-gray-900">
            View
          </button>
        </div>
      </div>

      {/* Filters - Side by side on all screen sizes */}
      <div className="flex items-center gap-4 mb-6">
        {/* Customer Filter */}
        <div className="relative flex-1 lg:flex-initial" ref={customerDropdownRef}>
          <button
            onClick={() => setIsCustomerDropdownOpen(!isCustomerDropdownOpen)}
            className="w-full lg:w-auto bg-gray-200 text-gray-800 px-4 py-2 rounded-lg font-semibold flex items-center justify-between hover:bg-gray-300 transition-colors lg:min-w-[200px]"
          >
            {getFilterCustomerDisplay(filters.customerId)}
            <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
            </svg>
          </button>
          {isCustomerDropdownOpen && (
            <div className="absolute z-10 mt-1 w-full bg-white rounded-md shadow-lg py-1 max-h-60 overflow-auto">
              <button
                onClick={() => {
                  handleFilterChange('customerId', '');
                  setIsCustomerDropdownOpen(false);
                }}
                className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
              >
                All Customers
              </button>
              {Object.values(customers).map(customer => (
                <button
                  key={customer.id}
                  onClick={() => {
                    handleFilterChange('customerId', customer.id);
                    setIsCustomerDropdownOpen(false);
                  }}
                  className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                >
                  {getCustomerDisplayName(customer)}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Date Filter Dropdown */}
        <div className="relative flex-1 lg:flex-initial" ref={datePresetDropdownRef}>
          <button
            onClick={() => setIsDatePresetDropdownOpen(!isDatePresetDropdownOpen)}
            className="w-full lg:w-auto bg-gray-200 text-gray-800 px-4 py-2 rounded-lg font-semibold flex items-center justify-between hover:bg-gray-300 transition-colors lg:min-w-[180px]"
          >
            {filters.datePreset ? (
              filters.datePreset === 'custom' ? 'Custom Dates' :
              filters.datePreset.charAt(0).toUpperCase() + filters.datePreset.slice(1)
            ) : 'All Dates'}
            <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
            </svg>
          </button>
          {isDatePresetDropdownOpen && (
            <div className="absolute z-10 mt-1 w-full bg-white rounded-md shadow-lg py-1">
              <button
                onClick={() => {
                  setFilters(prev => ({ ...prev, dateFrom: '', dateTo: '', datePreset: '' }));
                  setIsDatePresetDropdownOpen(false);
                }}
                className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
              >
                All Dates
              </button>
              <button
                onClick={() => {
                  setShowCustomDatesModal(true);
                  setTempDateRange({ from: filters.dateFrom, to: filters.dateTo });
                  setIsDatePresetDropdownOpen(false);
                }}
                className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
              >
                Custom Dates
              </button>
              {['today', 'week', 'month', 'quarter', 'year'].map(preset => (
                <button
                  key={preset}
                  onClick={() => {
                    handleDatePreset(preset);
                    setFilters(prev => ({ ...prev, datePreset: preset }));
                    setIsDatePresetDropdownOpen(false);
                  }}
                  className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                >
                  {preset.charAt(0).toUpperCase() + preset.slice(1)}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Date Range Display - Only show if dates are selected */}
        {(filters.dateFrom || filters.dateTo) && (
          <div className="hidden lg:flex items-center gap-2 text-sm text-gray-600">
            <span>{filters.dateFrom || 'mm/dd/yyyy'}</span>
            <span className="text-gray-500">to</span>
            <span>{filters.dateTo || 'mm/dd/yyyy'}</span>
          </div>
        )}
      </div>

      {/* Date Range Display for Mobile/Tablet - Show below filters if dates are selected */}
      {(filters.dateFrom || filters.dateTo) && (
        <div className="flex lg:hidden items-center gap-2 text-sm text-gray-600 mb-4">
          <span>{filters.dateFrom || 'mm/dd/yyyy'}</span>
          <span className="text-gray-500">to</span>
          <span>{filters.dateTo || 'mm/dd/yyyy'}</span>
        </div>
      )}

      {/* Custom Dates Modal */}
      {showCustomDatesModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-lg p-6 max-w-md w-full">
            <h3 className="text-lg font-semibold mb-4">Select Date Range</h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">From Date</label>
                <input
                  type="date"
                  value={tempDateRange.from}
                  onChange={(e) => setTempDateRange(prev => ({ ...prev, from: e.target.value }))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-brand"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">To Date</label>
                <input
                  type="date"
                  value={tempDateRange.to}
                  onChange={(e) => setTempDateRange(prev => ({ ...prev, to: e.target.value }))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-brand"
                />
              </div>
            </div>
            
            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => {
                  setTempDateRange({ from: '', to: '' });
                  setFilters(prev => ({ ...prev, dateFrom: '', dateTo: '', datePreset: '' }));
                  setShowCustomDatesModal(false);
                }}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
              >
                Reset
              </button>
              <button
                onClick={() => {
                  setFilters(prev => ({ 
                    ...prev, 
                    dateFrom: tempDateRange.from, 
                    dateTo: tempDateRange.to,
                    datePreset: 'custom'
                  }));
                  setShowCustomDatesModal(false);
                }}
                className="px-4 py-2 bg-brand text-white rounded-lg hover:bg-brand-hover font-semibold"
              >
                Apply
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirmation Modal */}
      {showConfirmModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-lg p-6 max-w-md w-full">
            <h3 className="text-lg font-semibold mb-4">
              {confirmAction === 'void' ? 'Void Invoice' : 'Delete Invoice'}
            </h3>
            
            <p className="text-gray-600 mb-6">
              {confirmAction === 'void' 
                ? `Are you sure you want to void invoice ${selectedInvoice?.invoiceNumber}? This will mark the invoice as voided but keep it in your records.`
                : `Are you sure you want to delete invoice ${selectedInvoice?.invoiceNumber}? This will remove it from your invoice list but keep it in the database for record-keeping.`
              }
            </p>
            
            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowConfirmModal(false);
                  setSelectedInvoice(null);
                  setConfirmAction(null);
                }}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmAction}
                className={`px-4 py-2 rounded-lg font-semibold ${
                  confirmAction === 'void' 
                    ? 'bg-orange-500 text-white hover:bg-orange-600' 
                    : 'bg-red-500 text-white hover:bg-red-600'
                }`}
              >
                {confirmAction === 'void' ? 'Void Invoice' : 'Delete Invoice'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-6">
          {error}
        </div>
      )}

      {/* Invoices Display - Table for desktop, Cards for mobile */}
      {invoices.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-8 text-center">
          <svg 
            className="mx-auto h-12 w-12 text-gray-400 mb-4" 
            fill="none" 
            stroke="currentColor" 
            viewBox="0 0 24 24"
          >
            <path 
              strokeLinecap="round" 
              strokeLinejoin="round" 
              strokeWidth="2" 
              d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" 
            />
          </svg>
          <h3 className="text-lg font-medium text-gray-900">No invoices yet</h3>
          <p className="mt-1 text-sm text-gray-500">Create your first invoice to get started</p>
          <PermissionGate permission={PERMISSIONS.invoices.create}>
            <Link
              to="/merchant/invoices/new"
              className="mt-4 inline-flex items-center px-4 py-2 bg-brand text-white rounded-lg font-semibold hover:bg-brand-hover transition-colors"
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
              </svg>
              Create Invoice
            </Link>
          </PermissionGate>
        </div>
      ) : (
        <>
          {/* Mobile View - Cards */}
          <div className="block lg:hidden">
            {invoices.map(invoice => renderMobileInvoiceCard(invoice))}
          </div>

          {/* Desktop View - Cards */}
          <div className="hidden lg:block">
            <div className="space-y-4">
              {invoices.map(invoice => renderDesktopInvoiceCard(invoice))}
            </div>
            
            {/* Desktop Pagination - only show if more than 25 items */}
            {pagination.totalItems > 25 && (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 mt-4 p-4">
                <div className="flex flex-col lg:flex-row items-center justify-between gap-4">
                  <div className="text-sm text-gray-700 text-center lg:text-left">
                    Showing {((pagination.page - 1) * pagination.limit) + 1} to {Math.min(pagination.page * pagination.limit, pagination.totalItems)} of {pagination.totalItems} results
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                      disabled={pagination.page === 1}
                      className="px-3 py-1 border border-gray-300 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
                    >
                      Previous
                    </button>
                    <button
                      onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                      disabled={pagination.page === pagination.totalPages}
                      className="px-3 py-1 border border-gray-300 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
                    >
                      Next
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Mobile Pagination - only show if more than 25 items */}
          {pagination.totalItems > 25 && (
            <div className="block lg:hidden mt-4">
              <div className="bg-white rounded-lg shadow p-4">
                <div className="text-sm text-gray-700 text-center mb-3">
                  Showing {((pagination.page - 1) * pagination.limit) + 1} to {Math.min(pagination.page * pagination.limit, pagination.totalItems)} of {pagination.totalItems} results
                </div>
                <div className="flex gap-2 justify-center">
                  <button
                    onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                    disabled={pagination.page === 1}
                    className="px-4 py-2 border border-gray-300 rounded-lg text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                    disabled={pagination.page === pagination.totalPages}
                    className="px-4 py-2 border border-gray-300 rounded-lg text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
                  >
                    Next
                  </button>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default InvoiceList;